/* =============================================================================
 * useFilterSections — shared filter primitive (KGH-795)
 * -----------------------------------------------------------------------------
 * Open/closed state for a set of FilterSections.
 *
 * `exclusive` (the default) is the athlete-side behaviour: opening one section
 * closes every other. That is right for college search, where the sections are
 * long and mostly independent. It is wrong for the coach grid, where a coach
 * narrows by hearts AND class AND position at once and would spend the whole
 * session re-opening sections — so the coach sidebar passes exclusive: false.
 * ========================================================================== */
import { useCallback, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

export interface FilterSectionsApi {
  expanded: Record<string, boolean>;
  isExpanded: (section: string) => boolean;
  toggle: (section: string) => void;
  setExpanded: Dispatch<SetStateAction<Record<string, boolean>>>;
}

export function useFilterSections(
  initial: Record<string, boolean>,
  opts?: { exclusive?: boolean }
): FilterSectionsApi {
  const exclusive = opts?.exclusive ?? true;
  const [expanded, setExpanded] = useState<Record<string, boolean>>(initial);

  const toggle = useCallback(
    (section: string) => {
      setExpanded((prev) => {
        if (!exclusive) return { ...prev, [section]: !prev[section] };
        // Close all sections first, then toggle the clicked one.
        const allClosed = Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: false }), {});
        return { ...allClosed, [section]: !prev[section] };
      });
    },
    [exclusive]
  );

  const isExpanded = useCallback((section: string) => !!expanded[section], [expanded]);

  return { expanded, isExpanded, toggle, setExpanded };
}

export default useFilterSections;
